/**
 * Home Page
 *
 * Landing page with elegant feature showcase
 */

import { ArrowRight, Eye, Monitor, Smartphone, Sparkles, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { buttonClasses, cn } from '../components/ui'

export default function HomePage() {
	const { t } = useTranslation()
	return (
		<div className="space-y-12">
			{/* Hero section */}
			<section className="animate-enter">
				<div className="card-refined p-8 sm:p-12 relative overflow-hidden">
					{/* Decorative background */}
					<div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-coral-500/10 to-sage-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
					<div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-primary/10 to-coral-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

					<div className="relative max-w-2xl">
						<div className="flex items-center gap-2 mb-4">
							<div className="decorative-line" />
							<span className="text-sm font-medium text-coral-700 uppercase tracking-wider">
								{t('home.tagline')}
							</span>
						</div>
						<h1 className="mb-4">
							<span className="text-base-content">Polygraf Online</span>
						</h1>
						<p className="text-lg text-base-content/70 leading-relaxed mb-8">
							{t('home.heroDescription')}
						</p>
						<div className="flex flex-wrap gap-4">
							<Link to="/spaces" className={cn(buttonClasses(), 'inline-flex items-center gap-2')}>
								{t('home.browseSpaces')}
								<ArrowRight size={18} />
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* Features section */}
			<section className="animate-enter stagger-3">
				<div className="flex items-center gap-3 mb-6">
					{/* This icon sits on the page background, which App gives `bg-base-200`
					    (cream[200] #f5f0e8). coral[500] there is 2.80:1 — fails SC 1.4.11
					    (3:1). coral[700] is 5.04:1. Dark theme keeps coral[500] (5.44:1 on
					    #161b22); coral[700] would fall to 3.02:1. The theme switch is
					    `data-theme` on <html>, not a `dark` class, so Tailwind's `dark:`
					    variant never matches — use the same arbitrary variant as
					    components/sso-providers.tsx. */}
					<Sparkles
						size={24}
						className="text-coral-700 [[data-theme=polygraf-dark]_&]:text-coral-500"
					/>
					<h2>{t('home.featuresTitle')}</h2>
				</div>
				<div className="grid md:grid-cols-2 gap-4">
					<FeatureCard
						icon={<Eye size={20} />}
						title={t('home.features.customDisplay')}
						description={t('home.features.customDisplayDesc')}
					/>
					<FeatureCard
						icon={<Monitor size={20} />}
						title={t('home.features.screenViewer')}
						description={t('home.features.screenViewerDesc')}
					/>
					<FeatureCard
						icon={<Zap size={20} />}
						title={t('home.features.cloudLocal')}
						description={t('home.features.cloudLocalDesc')}
					/>
					<FeatureCard
						icon={<Smartphone size={20} />}
						title={t('home.features.responsive')}
						description={t('home.features.responsiveDesc')}
					/>
				</div>
			</section>

			{/* CTA section */}
			<section className="animate-enter stagger-4">
				<div className="card-refined p-6 sm:p-8 bg-gradient-to-r from-primary to-primary/90 text-primary-content">
					<div className="flex flex-col sm:flex-row items-center justify-between gap-4">
						<div>
							<p className="text-xl font-semibold mb-1 text-primary-content">
								{t('home.ctaTitle')}
							</p>
							<p className="text-primary-content/80">{t('home.ctaDescription')}</p>
						</div>
						<Link
							to="/spaces"
							className={cn(
								buttonClasses({ variant: 'accent' }),
								'flex items-center gap-2 whitespace-nowrap',
							)}
						>
							<ArrowRight size={18} />
							{t('home.browseSpaces')}
						</Link>
					</div>
				</div>
			</section>
		</div>
	)
}

function FeatureCard({
	icon,
	title,
	description,
}: {
	icon: React.ReactNode
	title: string
	description: string
}) {
	return (
		<div className="card-refined p-5 flex gap-4">
			<div className="w-10 h-10 rounded-xl bg-gradient-to-br from-base-200 to-base-300 flex items-center justify-center flex-shrink-0">
				{/* Icon wrapper is a base-200 -> base-300 gradient. coral[500] measures
				    2.80:1 / 2.51:1 across it in light theme — fails SC 1.4.11 (3:1).
				    coral[700] measures 4.52:1 on base-300. Dark theme keeps coral[500]
				    (3.61:1 on #2d3a4f); coral[700] there would be 2.01:1. */}
				<div className="text-coral-700 [[data-theme=polygraf-dark]_&]:text-coral-500">{icon}</div>
			</div>
			<div>
				<p className="font-semibold mb-1">{title}</p>
				{/* MU 17. 9. 2026 (grey-on-cream): base-content (#1a2332) at /60 over
				    base-100 (cream[100] #faf8f5) measures 4.17:1 — fails WCAG 1.4.3 AA
				    for 14px body text (4.5:1). /70 measures 5.71:1. */}
				<p className="text-sm text-base-content/70">{description}</p>
			</div>
		</div>
	)
}
